import { observer } from 'mobx-react'
import { useContext, useEffect } from 'react'
import { Header, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, AGE_VERIFICATION_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect, withCommonQueryParams } from '@/methods/forceRedirect'

type PictureIdStatus = 'pending' | 'completed' | 'canceled' | 'failed'

interface PictureIdStatusResponse {
  status: PictureIdStatus
}

const POLL_INTERVAL_MS = 2000
// The ElectronicId result normally arrives within seconds; after this the workflow is treated as stuck
// and the user gets the failure screen with a retry instead of waiting forever.
const MAX_POLLING_MS = 3 * 60 * 1000

/**
 * The ElectronicId workflow sends the user here after the PictureID video. The verification can still
 * be running, so the page polls the BE until it settles: on success it moves on to the passkey
 * registration, otherwise it shows the failure screen of the registration page with a PictureID retry.
 */
export const AgeVerificationPictureIdPage = observer(() => {
  const store = useContext(StoreContext)
  const {
    ageVerificationPage: { pictureId: trans },
  } = store.TranslationsState.translations

  useEffect(() => {
    let pollTimeout: ReturnType<typeof setTimeout> | undefined
    // Set on unmount or on the deadline: a status request still in flight must neither re-poll nor
    // move the scenario on (StrictMode mounts the effect twice in dev, which would otherwise call
    // nextPage() twice).
    let isCancelled = false
    // Aborts the in-flight status request on unmount and on the deadline, so a single stalled
    // request cannot keep the user on this page past MAX_POLLING_MS.
    const abortController = new AbortController()

    const goToFailure = (status: 'canceled' | 'failed') =>
      forceRedirect(
        withCommonQueryParams(`/age-verification-return?status=${status}&method=pictureId`)
      )

    // Hard deadline, independent of whether the status requests ever answer
    const deadlineTimeout = setTimeout(() => {
      devLog('PictureID verification did not settle in time')
      isCancelled = true
      if (pollTimeout) clearTimeout(pollTimeout)
      abortController.abort()
      goToFailure('failed')
    }, MAX_POLLING_MS)

    const poll = async () => {
      try {
        const { data } = await axiosInstance.post<PictureIdStatusResponse>(
          `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/picture-id/status`,
          {},
          { withCredentials: true, signal: abortController.signal }
        )
        if (isCancelled) return

        if (data.status === 'pending') {
          pollTimeout = setTimeout(() => void poll(), POLL_INTERVAL_MS)
          return
        }

        clearTimeout(deadlineTimeout)
        if (data.status === 'completed') {
          store.ScenarioState.nextPage()
          return
        }
        // Anything else (canceled, failed, or a status this page does not know) is not a pass
        goToFailure(data.status === 'canceled' ? 'canceled' : 'failed')
      } catch (e) {
        // An aborted request was cancelled by this effect (unmount or deadline), which already
        // decided what happens next
        if (isCancelled) return
        clearTimeout(deadlineTimeout)
        devLog('Error reading the PictureID status:', e)
        forceRedirect(withCommonQueryParams('/error'))
      }
    }

    void poll()

    return () => {
      isCancelled = true
      clearTimeout(deadlineTimeout)
      if (pollTimeout) clearTimeout(pollTimeout)
      abortController.abort()
    }
  }, [])

  return (
    <div className="page-container mobile-m-0 vertically-centered">
      <LoadingSpinner />
      <Header className="m-0-0-30">{trans.waitingHeader}</Header>
      <p className="generic__paragraph-centered m-0-0-30">{trans.waitingDescription}</p>
    </div>
  )
})
